import { useCallback, useEffect, useReducer } from "react";

import api from "utils/api";
import { createContainer } from "utils/context";
import { PROPSOCH_ACTIONS } from "./actions";
import { authReducer, initialState } from "./reducers";
import {
  PROPERTY_PUBLISH_STATUS,
  PROPERTY_STATUS,
  RESPONSE,
  ROLES,
  USER_DATA,
  USER_ROLE,
} from "utils/constants";
import { ACTION_TYPE, MODULE, MODULE_NAMES } from "utils/logs.constants";
import { hydrate, persist } from "utils/persist";
import { Toastify } from "atoms";

export const { useContext: usePropsoch, Provider: PropsochProvider } =
  createContainer(() => {
    const [state, dispatch] = useReducer(authReducer, {
      ...initialState,
    });

    const addActivityLogs = useCallback(
      async (moduleName, module, action, propertyName) => {
        try {
          const reqData = {
            moduleName,
            module,
            actionType: action,
            propertyName,
          };
          const { data } = await api(`/admin/auth/track`, {
            method: "post",
            data: reqData,
          });

          return data?.status === RESPONSE.success && data?.data;
        } catch (e) {
          throw e;
        }
      },
      []
    );

    // location crud
    const getActivityLogs = useCallback(async (params) => {
      try {
        const { data } = await api("/admin/auth/activity", {
          method: "get",
          params,
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    // roles crud
    const getRoles = useCallback(async () => {
      try {
        const { data } = await api("/roles/get", {
          method: "get",
        });

        dispatch(PROPSOCH_ACTIONS.roles(data?.data));

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const createRole = useCallback(async (name) => {
      try {
        const reqData = { name };
        const { data } = await api("/roles/add", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const updateRole = useCallback(async (id, name) => {
      try {
        const reqData = { name };
        const { data } = await api(`/roles/update/${id}`, {
          method: "put",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const deleteRole = useCallback(async (id) => {
      try {
        const { data } = await api(`/roles/delete/${id}`, {
          method: "delete",
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // users crud
    const getUsers = useCallback(async () => {
      try {
        const { data } = await api("/admin/auth", {
          method: "get",
        });

        dispatch(PROPSOCH_ACTIONS.users(data?.data));

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const createUser = useCallback(async ({ name, email, password, role }) => {
      try {
        const reqData = { name, email, password, role };
        const { data } = await api("/admin/auth/admin", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const activateDeactivateUser = useCallback(async (userId, state) => {
      try {
        // const reqData = { name, email, password, role };
        const { data } = await api(
          `/admin/auth/acdc/${userId}?isActive=${state}`,
          {
            method: "post",
            // data: reqData,
          }
        );

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const deleteUser = useCallback(async (id) => {
      try {
        const { data } = await api(`/admin/auth/${id}`, {
          method: "delete",
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // location crud
    const getLocation = useCallback(async () => {
      try {
        const { data } = await api("/locations/get", {
          method: "get",
        });

        dispatch(PROPSOCH_ACTIONS.locations(data?.data));

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const createLocation = useCallback(async (name, isActive) => {
      try {
        const reqData = { name, isActive };
        const { data } = await api("/locations/add", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const updateLocation = useCallback(async (id, name, isActive) => {
      try {
        const reqData = { name, isActive };
        const { data } = await api(`/locations/update/${id}`, {
          method: "put",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    const deleteLocation = useCallback(async (id) => {
      try {
        const { data } = await api(`/locations/delete/${id}`, {
          method: "delete",
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // update property stepper
    const updatePropertyStepperStatus = useCallback(
      async ({ propertyId, ...props }) => {
        try {
          const reqData = {
            propertyId,
            ...props,
          };

          const { data } = await api(`/properties/update/flags/${propertyId}`, {
            method: "put",
            data: reqData,
          });

          return data?.data;
        } catch (e) {
          console.error(e);
        }
      },
      []
    );

    const getPropertyDetailsById = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/details/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    // location crud
    const getPropertyListing = useCallback(async (params) => {
      try {
        const { data } = await api("/properties/get", {
          method: "get",
          params,
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    // archived
    const getArchivePropertyListing = useCallback(async (params) => {
      try {
        const { data } = await api("/properties/get/archive", {
          method: "get",
          params,
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    // publish unpublish
    const changePublishStatus = useCallback(
      async (id, status, name, comment) => {
        try {
          if (status === PROPERTY_PUBLISH_STATUS.drafted) {
            const { data } = await api(`/properties/update/draft/${id}`, {
              method: "put",
              data: { status },
            });

            Toastify("success", `${name} drafted successfully`);
            return data?.data;
          } else if (status === PROPERTY_PUBLISH_STATUS.revert) {
            const { data } = await api(`/properties/update/revert/${id}`, {
              method: "put",
              data: { status: PROPERTY_PUBLISH_STATUS.drafted, comment },
            });
            Toastify("success", `${name} reverted successfully`);
            return data?.data;
          } else {
            const { data } = await api(`/properties/update/pub/${id}`, {
              method: "put",
              data: { status, comment },
            });

            status === PROPERTY_PUBLISH_STATUS.published
              ? addActivityLogs(
                  MODULE_NAMES.propertyPublish,
                  MODULE.properties,
                  ACTION_TYPE.update,
                  name
                )
              : addActivityLogs(
                  MODULE_NAMES.propertyUnpublish,
                  MODULE.properties,
                  ACTION_TYPE.update,
                  name
                );
            Toastify("success", `${name} ${status} successfully`);

            return data?.data;
          }
        } catch (e) {
          console.error(e);
          Toastify("error", `${name} ${status} failed`);
        }
      },
      [addActivityLogs]
    );
    // archive unarchive
    const changeArchiveStatus = useCallback(
      async (id, status, name) => {
        try {
          const { data } = await api(`/properties/update/archive/${id}`, {
            method: "put",
            data: { status },
          });

          status &&
            addActivityLogs(
              MODULE_NAMES.propertyDelete,
              MODULE.properties,
              ACTION_TYPE.delete,
              name
            );

          return data?.data;
        } catch (e) {
          console.error(e);
        }
      },
      [addActivityLogs]
    );

    // basic info
    const getPropertyListingBasicInfo = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/bi/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);
    const createPropertyListingBasicInfo = useCallback(
      async ({
        name,
        tagLine,
        propertyTotalNumberOfUnits,
        phaseTotalNumberOfUnits,
        reraNumber,
        propertyLandArea,
        phaseLandArea,
        possessionDate,
        locationId,
        micromarketId,
        developerId,
        images,
        statusId,
        types,
        typologies,
        propertyVideoUrl,
      }) => {
        try {
          const reqData = {
            name: name || "",
            tagLine: tagLine || "",
            propertyTotalNumberOfUnits: propertyTotalNumberOfUnits || 0,
            phaseTotalNumberOfUnits: phaseTotalNumberOfUnits || 0,
            reraNumber: reraNumber || "",
            propertyLandArea: propertyLandArea || "",
            phaseLandArea: phaseLandArea || "",
            possessionDate: possessionDate || "",
            locationId: locationId?.value || 0,
            micromarketId: micromarketId || 0,
            developerId: developerId?.value || 0,
            images: images || [],
            statusId: statusId || 0,
            types: types || [],
            typologies: typologies || [],
            propertyVideoUrl: propertyVideoUrl || "",
          };

          const { data } = await api("/properties/add/bi", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: data?.data?.id,
            [PROPERTY_STATUS.basicInfo]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyCreate,
            MODULE.properties,
            ACTION_TYPE.create,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingBasicInfo = useCallback(
      async (
        {
          name,
          tagLine,
          propertyTotalNumberOfUnits,
          phaseTotalNumberOfUnits,
          reraNumber,
          propertyLandArea,
          phaseLandArea,
          possessionDate,
          locationId,
          micromarketId,
          developerId,
          images,
          statusId,
          types,
          typologies,
          propertyVideoUrl,
        },
        id
      ) => {
        try {
          const reqData = {
            name: name || "",
            tagLine: tagLine || "",
            propertyTotalNumberOfUnits: propertyTotalNumberOfUnits || 0,
            phaseTotalNumberOfUnits: phaseTotalNumberOfUnits || 0,
            reraNumber: reraNumber || "",
            propertyLandArea: propertyLandArea || "",
            phaseLandArea: phaseLandArea || "",
            possessionDate: possessionDate || "",
            locationId: locationId?.value || 0,
            micromarketId: micromarketId || 0,
            developerId: developerId?.value || 0,
            images: images || [],
            statusId: statusId || 0,
            types: types || [],
            typologies: typologies || [],
            propertyVideoUrl: propertyVideoUrl || "",
          };

          const { data } = await api(`/properties/update/bi/${id}`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: data?.data?.id,
            [PROPERTY_STATUS.basicInfo]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // neighbourhood info
    const getPropertyListingNeighbourhood = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/ngh/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingNeighbourhood = useCallback(
      async (
        {
          propertyId,
          longitude,
          latitude,
          propertyAreaType,
          nearbyAreas,
          nearbyAreaComments,
          neighbourhoodImage,
          neighbourhoodImageAlt,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            longitude,
            latitude,
            propertyAreaType,
            nearbyAreas,
            nearbyAreaComments,
            neighbourhoodImage,
            neighbourhoodImageAlt,
          };

          const { data } = await api("/properties/add/ngh", {
            method: "post",
            data: reqData,
          });
          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.neighbourhood]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingNeighbourhood = useCallback(
      async (
        {
          propertyId,
          longitude,
          latitude,
          propertyAreaType,
          nearbyAreas,
          newImages,
          deletedImages,
          nearbyAreaComments,
          neighbourhoodImage,
          neighbourhoodImageAlt,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            longitude,
            latitude,
            propertyAreaType,
            nearbyAreas,
            newImages,
            deletedImages,
            nearbyAreaComments,
            neighbourhoodImage,
            neighbourhoodImageAlt,
          };

          const { data } = await api(`/properties/update/ngh/${id}`, {
            method: "put",
            data: reqData,
          });
          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.neighbourhood]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // social infra
    const getPropertyListingSocialInfra = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/infra/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingSocialInfra = useCallback(
      async ({ propertyId, infrastructure }, name) => {
        try {
          const reqData = {
            propertyId,
            infrastructure,
          };

          const { data } = await api("/properties/add/infra", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.socialInfra]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingSocialInfra = useCallback(
      async ({ propertyId, infrastructure }, id, name) => {
        try {
          const reqData = {
            propertyId,
            infrastructure,
          };

          const { data } = await api(`/properties/update/infra/${id}`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.socialInfra]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // specifications
    const getPropertyListingSpecifications = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/spec/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingSpecifications = useCallback(
      async (
        {
          propertyId,
          constructionTechnology,
          specificationIds,
          specificationComments,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            constructionTechnology,
            specificationIds,
            specificationComments,
          };

          const { data } = await api("/properties/add/spec", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.specifications]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );
    const updatePropertyListingSpecifications = useCallback(
      async (
        {
          propertyId,
          constructionTechnology,
          specificationIds,
          specificationComments,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            constructionTechnology,
            specificationIds,
            specificationComments,
          };

          const { data } = await api(`/properties/update/spec`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.specifications]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );

    // amenities
    const getPropertyListingAmenities = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/amenity/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingAmenities = useCallback(
      async (
        {
          propertyId,
          amenityIds,
          images,
          // totalNumberOfCourts,
          // numberOfIndoorCourts,
          // numberOfOutdoorCourts,
          clubHouseArea,
          numberOfClubHouse,
          amenityComments,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            amenityIds,
            images,
            // totalNumberOfCourts,
            // numberOfIndoorCourts,
            // numberOfOutdoorCourts,
            clubHouseArea,
            numberOfClubHouse,
            amenityComments,
          };

          const { data } = await api("/properties/add/amenity", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.amenities]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingAmenities = useCallback(
      async (
        {
          propertyId,
          amenityIds,
          images,
          // totalNumberOfCourts,
          // numberOfIndoorCourts,
          // numberOfOutdoorCourts,
          clubHouseArea,
          numberOfClubHouse,
          amenityComments,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            amenityIds,
            images,
            // totalNumberOfCourts,
            // numberOfIndoorCourts,
            // numberOfOutdoorCourts,
            clubHouseArea,
            numberOfClubHouse,
            amenityComments,
          };

          const { data } = await api(`/properties/update/amenity`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.amenities]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // compliances
    const getPropertyListingCompliances = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/compliances/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingCompliances = useCallback(
      async ({ propertyId, compliances }, name) => {
        try {
          const reqData = {
            propertyId,
            compliances,
          };

          const { data } = await api("/properties/add/compliances", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.compliance]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    const updatePropertyListingCompliances = useCallback(
      async ({ propertyId, compliances }, id, name) => {
        try {
          const reqData = {
            propertyId,
            compliances,
          };

          const { data } = await api(`/properties/update/compliances`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.compliance]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // legal
    const getPropertyListingLegal = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/legal/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingLegal = useCallback(
      async ({ propertyId, legal }, name) => {
        try {
          const reqData = {
            propertyId,
            legal,
          };

          const { data } = await api("/properties/add/legal", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.legal]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    const updatePropertyListingLegal = useCallback(
      async ({ propertyId, legal }, id, name) => {
        try {
          const reqData = {
            propertyId,
            legal,
          };

          const { data } = await api(`/properties/update/legal`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.legal]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // connectivity
    const getPropertyListingConnectivity = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/connectivity/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingConnectivity = useCallback(
      async (
        {
          propertyId,
          accessRoadWidth,
          roadImages,
          qualityOfAccessRoad,
          transportations,
          connectivityComments,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            accessRoadWidth,
            roadImages,
            qualityOfAccessRoad,
            transportations,
            connectivityComments,
          };

          const { data } = await api("/properties/add/connectivity", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.connectivity]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingConnectivity = useCallback(
      async (
        {
          propertyId,
          accessRoadWidth,
          roadImages,
          qualityOfAccessRoad,
          transportations,
          connectivityComments,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            accessRoadWidth,
            roadImages,
            qualityOfAccessRoad,
            transportations,
            connectivityComments,
          };

          const { data } = await api(`/properties/update/connectivity/${id}`, {
            method: "put",
            data: reqData,
          });
          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.connectivity]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // pricing
    const getPropertyListingPricing = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/pricing/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingPricing = useCallback(
      async ({ propertyId, pricing }, name) => {
        try {
          const reqData = {
            propertyId,
            pricing,
          };

          const { data } = await api("/properties/add/pricing", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.pricing]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingPricing = useCallback(
      async ({ propertyId, pricing }, id, name) => {
        try {
          const reqData = {
            propertyId,
            pricing,
          };

          const { data } = await api(`/properties/update/pricing/${id}`, {
            method: "put",
            data: reqData,
          });
          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.pricing]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // market place
    const getPropertyListingMarketPlace = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/mp/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingMarketPlace = useCallback(
      async (
        {
          propertyId,
          openArea,
          projectNumberOfUnits,
          // projectLandArea,
          densityOfUnits,
          achievedFAR,
          floorToCeilingHeight,
          parkArea,
          masterPlanImages,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            openArea,
            projectNumberOfUnits,
            // projectLandArea,
            densityOfUnits,
            achievedFAR,
            floorToCeilingHeight,
            parkArea,
            masterPlanImages,
          };

          const { data } = await api("/properties/add/mp", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.masterPlan]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );
    const updatePropertyListingMarketPlace = useCallback(
      async (
        {
          propertyId,
          openArea,
          projectNumberOfUnits,
          // projectLandArea,
          densityOfUnits,
          achievedFAR,
          floorToCeilingHeight,
          parkArea,
          masterPlanImages,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            openArea,
            projectNumberOfUnits,
            // projectLandArea,
            densityOfUnits,
            achievedFAR,
            floorToCeilingHeight,
            parkArea,
            masterPlanImages,
          };

          const { data } = await api(`/properties/update/mp`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.masterPlan]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );
    // site challanges
    const getPropertyListingSiteChallanges = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/sc/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingSiteChallanges = useCallback(
      async (
        {
          propertyId,
          htLines,
          nala,
          nalaType,
          owc,
          garbageCollectorPlant,
          wasteManagementUnit,
          roadCuttingThrough,
          metres,
          gas,
          burialGround,
          kharabLand,
          siteImages,
        },
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            htLines,
            nala,
            nalaType,
            owc,
            garbageCollectorPlant,
            wasteManagementUnit,
            roadCuttingThrough,
            metres,
            gas,
            burialGround,
            kharabLand,
            siteImages,
          };

          const { data } = await api("/properties/add/sc", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.siteChallanges]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );
    const updatePropertyListingSiteChallanges = useCallback(
      async (
        {
          propertyId,
          htLines,
          nala,
          nalaType,
          owc,
          garbageCollectorPlant,
          wasteManagementUnit,
          roadCuttingThrough,
          metres,
          gas,
          burialGround,
          kharabLand,
          siteImages,
        },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            htLines,
            nala,
            nalaType,
            owc,
            garbageCollectorPlant,
            wasteManagementUnit,
            roadCuttingThrough,
            metres,
            gas,
            burialGround,
            kharabLand,
            siteImages,
          };

          const { data } = await api(`/properties/update/sc`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.siteChallanges]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs, updatePropertyStepperStatus]
    );

    // tower unit plans
    const getPropertyListingTowerUnitPlan = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/get/tup/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingTowerUnitPlan = useCallback(
      async ({ propertyId, numberOfTowers, towers, towerPlanImages }, name) => {
        try {
          const reqData = {
            propertyId,
            numberOfTowers,
            towers,
            towerPlanImages,
          };

          const { data } = await api("/properties/add/tup", {
            method: "post",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.towerUnitPlan]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );
    const updatePropertyListingTowerUnitPlan = useCallback(
      async (
        { propertyId, numberOfTowers, towers, towerPlanImages },
        id,
        name
      ) => {
        try {
          const reqData = {
            propertyId,
            numberOfTowers,
            towers,
            towerPlanImages,
          };

          const { data } = await api(`/properties/update/tup`, {
            method: "put",
            data: reqData,
          });

          await updatePropertyStepperStatus({
            propertyId: propertyId,
            [PROPERTY_STATUS.towerUnitPlan]: true,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [updatePropertyStepperStatus, addActivityLogs]
    );

    // unit details
    const getPropertyListingTowerUnitDetails = useCallback(async (id) => {
      try {
        const { data } = await api(`/properties/unit-plan/get/${id}`, {
          method: "get",
        });

        return data?.data;
      } catch (e) {
        console.error(e);
      }
    }, []);

    const createPropertyListingTowerUnitDetails = useCallback(
      async ({ propertyId, towerName, file }, name) => {
        try {
          const formData = new FormData();
          formData.append("propertyId", propertyId);
          formData.append("towerName", towerName);
          formData.append("file", file);

          const { data } = await api("/properties/unit-plan/upload", {
            method: "post",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: formData,
          });

          addActivityLogs(
            MODULE_NAMES.propertyUpdate,
            MODULE.properties,
            ACTION_TYPE.update,
            name
          );

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      [addActivityLogs]
    );

    const createPropertyListingTowerUnitDetailsUploadTower = useCallback(
      async ({ propertyId, towerName, url }) => {
        try {
          const reqData = {
            propertyId,
            towerName,
            url,
          };

          const { data } = await api("/upload-history/add", {
            method: "post",
            data: reqData,
          });

          return data?.data;
        } catch (e) {
          console.error(e);
          throw e;
        }
      },
      []
    );

    // unit details
    const getPropertyListingTowerUnitDetailsUploadTower = useCallback(
      async (id) => {
        try {
          const { data } = await api(`/upload-history/get/${id}`, {
            method: "get",
          });

          return data?.data;
        } catch (e) {
          console.error(e);
        }
      },
      []
    );

    // calculate pricing
    const calculatePricing = useCallback(
      async ({ propertyId, ticketPrice, typologyName, reraSaleableArea }) => {
        try {
          const reqData = {
            propertyId,
            ticketPrice,
            typologyName,
            reraSaleableArea,
          };
          const { data } = await api(`/properties/pricing/calculate`, {
            method: "post",
            data: reqData,
          });

          return data?.status === RESPONSE.success && data?.data;
        } catch (e) {
          throw e;
        }
      },
      []
    );

    const manageUserRoleCookie = useCallback(() => {
      const userData = hydrate(USER_DATA);
      const roleId = userData?.roleId;

      const userRole = { role: state?.roles?.find((i) => i?.id === roleId) };
      userRole.isSuperadmin =
        userRole?.role?.name?.toUpperCase() === ROLES.superadmin?.toUpperCase();
      userRole.isAdmin =
        userRole?.role?.name?.toUpperCase() === ROLES.admin?.toUpperCase();
      userRole.isMaker =
        userRole?.role?.name?.toUpperCase() === ROLES.maker?.toUpperCase();
      userRole.isChecker =
        userRole?.role?.name?.toUpperCase() === ROLES.checker?.toUpperCase();

      persist(USER_ROLE, userRole);
      dispatch(PROPSOCH_ACTIONS.userRole(userRole));
    }, [state?.roles]);

    useEffect(() => {
      getRoles();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      manageUserRoleCookie();
    }, [manageUserRoleCookie]);

    return {
      state,
      actions: {
        // roles
        getRoles,
        createRole,
        updateRole,
        deleteRole,
        // users
        getUsers,
        createUser,
        activateDeactivateUser,
        deleteUser,
        // locations
        getLocation,
        createLocation,
        updateLocation,
        deleteLocation,
        // publish status,
        changePublishStatus,
        // archive unarchive
        changeArchiveStatus,
        // property listing
        getPropertyListing,
        // archived
        getArchivePropertyListing,
        // Basic Info
        getPropertyListingBasicInfo,
        createPropertyListingBasicInfo,
        updatePropertyListingBasicInfo,
        // neighbourhood info
        getPropertyListingNeighbourhood,
        createPropertyListingNeighbourhood,
        updatePropertyListingNeighbourhood,
        // social infra
        getPropertyListingSocialInfra,
        createPropertyListingSocialInfra,
        updatePropertyListingSocialInfra,
        // specifications
        getPropertyListingSpecifications,
        createPropertyListingSpecifications,
        updatePropertyListingSpecifications,
        // amenities
        getPropertyListingAmenities,
        createPropertyListingAmenities,
        updatePropertyListingAmenities,
        // compliances
        getPropertyListingCompliances,
        createPropertyListingCompliances,
        updatePropertyListingCompliances,
        // legal
        getPropertyListingLegal,
        createPropertyListingLegal,
        updatePropertyListingLegal,
        // connectivity
        getPropertyListingConnectivity,
        createPropertyListingConnectivity,
        updatePropertyListingConnectivity,
        // pricing
        getPropertyListingPricing,
        createPropertyListingPricing,
        updatePropertyListingPricing,
        // market place
        getPropertyListingMarketPlace,
        createPropertyListingMarketPlace,
        updatePropertyListingMarketPlace,
        // site challanges
        getPropertyListingSiteChallanges,
        createPropertyListingSiteChallanges,
        updatePropertyListingSiteChallanges,
        // tower unit plans
        getPropertyListingTowerUnitPlan,
        createPropertyListingTowerUnitPlan,
        updatePropertyListingTowerUnitPlan,
        // unit details
        getPropertyListingTowerUnitDetails,
        createPropertyListingTowerUnitDetails,
        createPropertyListingTowerUnitDetailsUploadTower,
        getPropertyListingTowerUnitDetailsUploadTower,
        // stepper status
        getPropertyDetailsById,
        updatePropertyStepperStatus,
        // calculate pricing,
        calculatePricing,
        // activity logs
        addActivityLogs,
        getActivityLogs,
      },
    };
  });
